import React, { useState, useEffect, useRef } from "react";
import Header from "../../../Componets/Header";
import toast from "react-hot-toast";
import moment from "moment";
import { get_all_invoices } from "../../../Controllers/Sales/Invoices";
import { get_all_sales_returns } from "../../../Controllers/Sales/Returns";
import { get_all_purchases } from "../../../Controllers/Purchases/Orders";
import { get_all_purchases_returns } from "../../../Controllers/Purchases/Returns";
import { get_all_expenses } from "../../../Controllers/Expenses/Expenses";

import ReactToPrint from "react-to-print";
import { Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import Loadercomponent from "../../../Componets/Loader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerCairoFont } from "../cairo-font";
import ScrollWrapper from "../../../Componets/ScrollWrapper";
import formatDateDDMMYYYY from "../../../Main/formatDate";

const SalesReportReplica = () => {
  const [loader, setLoader] = useState(false);
  const [reportData, setReportData] = useState(null);
  const componentRef = useRef(null);

  const [header, setHeader] = useState({
    name: "Daily Transaction Report",
    components: [
      {
        type: "range",
        name: "date",
        value: {
          start: moment().format("YYYY-MM-DD"),
          end: moment().format("YYYY-MM-DD"),
        },
      },
    ],
    navigate: {
      tabs: [
        { name: "Reports", path: "/app/reports" },
        { name: "Transaction Report", path: "" },
      ],
    },
  });

  const fetchData = async (tabledata) => {
    try {
      setLoader(true);
      const data = {
        date: tabledata?.date,
        source: "Transaction Report",
      };

      const [invRes, sRetRes, purRes, pRetRes, expRes] = await Promise.all([
        get_all_invoices(data),
        get_all_sales_returns(data),
        get_all_purchases(data),
        get_all_purchases_returns(data),
        get_all_expenses(data),
      ]);

      processReportData(
        invRes?.data?.data || [],
        sRetRes?.data?.data || [],
        purRes?.data?.data || [],
        pRetRes?.data?.data || [],
        expRes?.data?.data || [],
      );
      setLoader(false);
    } catch (errors) {
      toast.error(errors?.message || "Data fetch error");
      setLoader(false);
    }
  };

  const processReportData = (
    invoices,
    salesReturns,
    purchases,
    purReturns,
    expenses,
  ) => {
    const n = (v) => Number(v || 0);

    // 1. Paid items for Cash Flow
    const paidInvoices = invoices.filter(
      (i) => i.payment_status === 1 || i.payment_status === 2,
    );
    const paidSalesReturns = salesReturns.filter(
      (r) => r.payment_status === 1 || r.payment_status === 2,
    );

    const paidPurchases = purchases.filter(
      (p) => p.payment_status === 1 || p.payment_status === 2,
    );

    const paidPurReturns = purReturns.filter(
      (pr) => pr.payment_status === 1 || pr.payment_status === 2,
    );
    const paidExpenses = expenses.filter(
      (e) => e.status === 0 || e.payment_status === 2,
    );

    // 2. Credit items (Unpaid or Partial)
    const creditInvoices = invoices.filter((i) => i.payment_status !== 2);
    const creditPurchasesList = purchases.filter((p) => p.payment_status !== 2);

    // 3. Paid Totals
    const totalSales = paidInvoices.reduce(
      (sum, inv) =>
        sum + (inv.payments?.reduce((s, p) => s + n(p.amount), 0) || 0),
      0,
    );
    const totalSalesRet = paidSalesReturns.reduce(
      (sum, r) => sum + n(r.paid),
      0,
    );
    const netSales = totalSales - totalSalesRet;

    const totalPurchases = paidPurchases.reduce(
      (sum, p) =>
        sum + (p.payments?.reduce((s, pay) => s + n(pay.amount), 0) || 0),
      0,
    );
    const totalPurReturns = paidPurReturns.reduce(
      (sum, r) => sum + n(r.paid),
      0,
    );
    const netPurchases = totalPurchases - totalPurReturns;

    const totalExp = paidExpenses.reduce((sum, e) => sum + n(e.amount), 0);

    // 4. Credit Totals
    const totalCreditSales = creditInvoices.reduce(
      (sum, inv) => sum + (n(inv.total) - n(inv.paid)),
      0,
    );
    const totalCreditPurchases = creditPurchasesList.reduce(
      (sum, pur) => sum + n(pur.balance || pur.total - pur.paidAmount),
      0,
    );

    // 5. Methods Mapping
    const receivedMethods = {};
    paidInvoices.forEach((inv) => {
      inv.payments?.forEach((p) => {
        receivedMethods[p.name] = n(receivedMethods[p.name]) + n(p.amount);
      });
    });

    const madeMethods = {};
    paidPurchases.forEach((pur) => {
      pur.payments?.forEach((p) => {
        madeMethods[p.name] = n(madeMethods[p.name]) + n(p.amount);
      });
    });
    paidExpenses.forEach((exp) => {
      const method = exp.payment || "Cash";
      madeMethods[method] = n(madeMethods[method]) + n(exp.amount);
    });

    const inflowList = Object.keys(receivedMethods).map((m) => ({
      method: m,
      amount: receivedMethods[m],
    }));
    const outflowList = Object.keys(madeMethods).map((m) => ({
      method: m,
      amount: madeMethods[m],
    }));
    if (totalSalesRet > 0)
      inflowList.push({ method: "Refunds (Returns)", amount: -totalSalesRet });

    const salesTax =
      paidInvoices.reduce((sum, i) => sum + n(i.taxamount), 0) -
      paidSalesReturns.reduce((sum, r) => sum + n(r.taxamount), 0);
    const purTax =
      paidPurchases.reduce((sum, p) => sum + n(p.taxamount), 0) -
      paidPurReturns.reduce((sum, r) => sum + n(r.taxamount), 0);
    const salesDisc = paidInvoices.reduce((sum, i) => sum + n(i.discount), 0);
    const purDisc = paidPurchases.reduce((sum, p) => sum + n(p.discount), 0);

    const dateRange = header.components.find((c) => c.name === "date")?.value;

    setReportData({
      dateRange:
        dateRange.start === dateRange.end
          ? formatDateDDMMYYYY(dateRange.start)
          : `${formatDateDDMMYYYY(dateRange.start)} - ${formatDateDDMMYYYY(dateRange.end)}`,
      sales: [
        {
          name: "Gross Sales",
          qty: paidInvoices.length,
          amount: totalSales,
        },
        {
          name: "Returns",
          qty: paidSalesReturns.length,
          amount: -totalSalesRet,
        },
        {
          name: "Net Sales",
          amount: netSales,
        },
      ],
      purchases: [
        {
          name: "Gross Purchases",
          qty: paidPurchases.length,
          amount: totalPurchases,
        },
        {
          name: "Returns",
          qty: paidPurReturns.length,
          amount: -totalPurReturns,
        },
        {
          name: "Net Purchases",
          amount: netPurchases,
        },
      ],
      creditSummary: {
        sales: totalCreditSales,
        salesCount: creditInvoices.length,
        purchases: totalCreditPurchases,
        purchasesCount: creditPurchasesList.length,
      },
      expenses: {
        qty: paidExpenses.length,
        amount: totalExp,
      },
      taxes: {
        salesTax,
        purTax,
        salesDisc,
        purDisc,
      },
      paymentsIn: inflowList,
      paymentsOut: outflowList,
      session: {
        salesCount: paidInvoices.length,
        purCount: paidPurchases.length,
        expCount: paidExpenses.length,
        netSales,
        netPurchases,
        totalExp,
        endOfDayPosition: netSales - netPurchases - totalExp,
      },
    });
  };

  useEffect(() => {
    const initialDate = header.components[0].value;
    fetchData({ date: initialDate });
  }, []);

  // const downloadPDF = () => {
  //   if (!reportData?.session) return;
  //   const doc = new jsPDF();
  //   registerCairoFont(doc);
  //   doc.setFont("Cairo-Bold", "bold");
  //   doc.text("Daily Transaction Report", 105, 15, { align: "center" });

  //   autoTable(doc, {
  //     startY: 25,
  //     head: [["Description", "Amount"]],
  //     body: [
  //       ["Net Sales (+)", formatCurrencyPDF(reportData.session.netSales)],
  //       [
  //         "Net Purchases (-)",
  //         formatCurrencyPDF(reportData.session.netPurchases),
  //       ],
  //       ["Total Expenses (-)", formatCurrencyPDF(reportData.session.totalExp)],
  //       [
  //         "Unpaid Sales (Credit)",
  //         formatCurrencyPDF(reportData.creditSummary.sales),
  //       ],
  //       [
  //         "Unpaid Purchases (Credit)",
  //         formatCurrencyPDF(reportData.creditSummary.purchases),
  //       ],
  //       [
  //         "EXPECTED BALANCE",
  //         formatCurrencyPDF(reportData.session.endOfDayPosition),
  //       ],
  //     ],
  //     theme: "grid",
  //     styles: {
  //       font: "Cairo",
  //     },
  //   });

  //   const paymentRows = [];
  //   const maxLen = Math.max(
  //     reportData.paymentsIn.length,
  //     reportData.paymentsOut.length,
  //   );
  //   for (let i = 0; i < maxLen; i++) {
  //     const pin = reportData.paymentsIn[i];
  //     const pout = reportData.paymentsOut[i];
  //     paymentRows.push([
  //       pin ? `${pin.method}: ${formatCurrency(pin.amount)}` : "",
  //       pout ? `${pout.method}: ${formatCurrency(pout.amount)}` : "",
  //     ]);
  //   }

  //   autoTable(doc, {
  //     head: [["Payments Received (In)", "Payments Made (Out)"]],
  //     body: paymentRows,
  //     theme: "striped",
  //     styles: {
  //       font: "Cairo",
  //     },
  //   });

  //   doc.save(`Report_${reportData.dateRange}.pdf`);
  // };

  if (loader || !reportData || !reportData.session) {
    return (
      <Loadercomponent loader={loader}>
        <div className="p-10 text-center">Loading Report...</div>
      </Loadercomponent>
    );
  }

  return (
    <Header headers={header} setHeaders={setHeader} formdata={fetchData}>
      <div className="text-right flex gap-3 justify-end mt-4 px-6">
        <ReactToPrint
          trigger={() => (
            <Button type="primary">
              <PrinterOutlined />
              Print Report
            </Button>
          )}
          content={() => componentRef.current}
          pageStyle={`
    @page {
      size: A4;
      margin: 12mm;
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 10px;
      }

      th, td {
        border: 1px solid #000;
        padding: 4px;
      }

      thead {
        display: table-header-group;
      }

      tfoot {
        display: table-footer-group;
      }
    }
  `}
        />
      </div>

      <ScrollWrapper>
        <div
          ref={componentRef}
          className="max-w-3xl mx-auto p-6 bg-white shadow-md rounded-xl mt-4"
        >
          <header className="text-center mb-6">
            <h3 className="text-xl font-bold">Daily Transaction Report</h3>
            <p className="text-gray-500">Date: {reportData.dateRange}</p>
          </header>

          <div className="space-y-6">
            {/* Sales Summary */}
            <section>
              <h4 className="text-xs font-bold uppercase text-blue-600 border-b pb-1 mb-2">
                Sales Summary (Paid)
              </h4>
              {reportData.sales.map((s, i) => (
                <div
                  key={i}
                  className={`flex justify-between py-1 text-sm ${s.name === "Net Sales" ? "font-bold border-t mt-1" : ""}`}
                >
                  <span>
                    {s.name} {s.qty ? `(${s.qty})` : ""}
                  </span>
                  <span>{formatCurrency(s.amount)}</span>
                </div>
              ))}
            </section>

            {/* Purchase Summary */}
            <section>
              <h4 className="text-xs font-bold uppercase text-orange-600 border-b pb-1 mb-2">
                Purchase Summary (Paid)
              </h4>
              {reportData.purchases.map((p, i) => (
                <div
                  key={i}
                  className={`flex justify-between py-1 text-sm ${p.name === "Net Purchases" ? "font-bold border-t mt-1" : ""}`}
                >
                  <span>
                    {p.name} {p.qty ? `(${p.qty})` : ""}
                  </span>
                  <span>{formatCurrency(p.amount)}</span>
                </div>
              ))}
            </section>

            {/* Expenses Section */}
            <section>
              <h4 className="text-xs font-bold uppercase text-red-600 border-b pb-1 mb-2">
                Expenses (Paid)
              </h4>
              <div className="flex justify-between py-1 text-sm font-bold">
                <span>Total Expenses ({reportData.expenses.qty})</span>
                <span>{formatCurrency(reportData.expenses.amount)}</span>
              </div>
            </section>

            {/* Credit Summary Section */}
            <section>
              <h4 className="text-xs font-bold uppercase text-gray-500 border-b pb-1 mb-2">
                Credit Summary (Unpaid)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between p-2 text-sm bg-gray-50 rounded border border-gray-100">
                  <span>Sales ({reportData.creditSummary.salesCount})</span>
                  <span className="font-bold">
                    {formatCurrency(reportData.creditSummary.sales)}
                  </span>
                </div>
                <div className="flex justify-between p-2 text-sm bg-gray-50 rounded border border-gray-100">
                  <span>
                    Purchases ({reportData.creditSummary.purchasesCount})
                  </span>
                  <span className="font-bold">
                    {formatCurrency(reportData.creditSummary.purchases)}
                  </span>
                </div>
              </div>
            </section>

            {/* Cash Flow Tables */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                <h5 className="text-[10px] font-bold uppercase mb-2 text-green-700">
                  Money In (Received)
                </h5>
                {reportData.paymentsIn.map((p, i) => (
                  <div
                    key={i}
                    className={`flex justify-between text-xs py-0.5 ${p.method.includes("Refund") ? "text-red-500" : ""}`}
                  >
                    <span>{p.method}:</span> <b>{formatCurrency(p.amount)}</b>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <h5 className="text-[10px] font-bold uppercase mb-2 text-red-700">
                  Money Out (Given From)
                </h5>
                {reportData.paymentsOut.map((p, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span>{p.method}:</span> <b>{formatCurrency(p.amount)}</b>
                  </div>
                ))}
              </div>
            </div>

            {/* Taxes & Discounts */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <h5 className="text-[10px] font-bold uppercase mb-2">
                Taxes & Discounts Summary
              </h5>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Net Sales Tax:</span>
                  <b>{formatCurrency(reportData.taxes?.salesTax)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Sales Disc:</span>
                  <b>{formatCurrency(reportData.taxes?.salesDisc)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Net Pur. Tax:</span>
                  <b>{formatCurrency(reportData.taxes?.purTax)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Pur. Disc:</span>
                  <b>{formatCurrency(reportData.taxes?.purDisc)}</b>
                </div>
              </div>
            </div>

            {/* Session Control */}
            <section className="p-4 border-2 border-dashed rounded-lg bg-green-50 border-green-200">
              <h4 className="text-sm font-bold mb-3 text-green-800 text-center uppercase tracking-wider">
                Session Control
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Transaction Counts:</span>
                  <span>
                    Sales: {reportData.session.salesCount} | Pur:{" "}
                    {reportData.session.purCount} | Exp:{" "}
                    {reportData.session.expCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Net Sales (+):</span>
                  <b>{formatCurrency(reportData.session.netSales)}</b>
                </div>
                <div className="flex justify-between">
                  <span>Net Purchases (-):</span>
                  <b className="text-red-500">
                    {formatCurrency(reportData.session.netPurchases)}
                  </b>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span>Total Expenses (-):</span>
                  <b className="text-red-500">
                    {formatCurrency(reportData.session.totalExp)}
                  </b>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="font-bold text-base">
                    Total Transaction Amount:
                  </span>
                  <span className="font-bold text-xl text-green-700">
                    {formatCurrency(reportData.session.endOfDayPosition)} OMR
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </ScrollWrapper>
    </Header>
  );
};

function formatCurrency(v) {
  return Number(v || 0).toFixed(3);
}
function formatCurrencyPDF(v) {
  return `${Number(v || 0).toFixed(3)} OMR`;
}

export default SalesReportReplica;
